import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import Confirm from "../../../../../../../Components/Popups/Confirm";

// Package-row fields a user (or a product pick) fills in. A new row has all
// of them as "" (Ship.jsx initial_Plist).
const ROW_FIELDS = [
  "name",
  "length",
  "width",
  "height",
  "weight",
  "insurance",
  "description",
  "feright",
  "nmfc",
  "type",
  "pieces",
];

const rowHasData = (row) =>
  ROW_FIELDS.some((key) => {
    const value = row?.[key];
    return value !== undefined && value !== null && String(value).trim() !== "";
  });

// How a location is named on the card and in the question below.
export const locationName = (entry) =>
  entry?.warehouse?.name || entry?.warehouse?.code || `Location #${entry?.warehouse_id}`;

// The writes the old Warehouse dropdown in My Products made (removed
// 2026-10-17; the card makes them now), in the same order: selected products cleared, the package rows replaced by one
// new empty row, then StepCheck.Warehouses set (KNOWN_ISSUES.md SP-035: the
// quote is left as it is).
export function applyWarehouseChange(
  value,
  { setSelectedProducts, SetPlist, SetStepCheck },
) {
  setSelectedProducts([]);
  SetPlist([
    {
      name: "",
      length: "",
      width: "",
      height: "",
      weight: "",
      volume: "",
      insurance: "",
      description: "",
      feright: "",
      nmfc: "",
      type: "",
      pieces: "",
      link_key: "",
    },
  ]);
  SetStepCheck((prevStepCheck) => ({
    ...prevStepCheck,
    Warehouses: value,
  }));
}

// The Fulfilment Location card's change (until 2026-10-17 the My Products
// dropdown used it too). A change that would remove products or filled
// package rows the user can see is
// confirmed first; anything else is applied at once, as before. Confirming
// makes exactly the writes above. Selected products only count while they
// are on screen (Package.jsx shows them only with a warehouse chosen): after
// Ship More they stay in memory unseen (SP-011), and clearing them asks
// nothing, as the dropdown always did.
//
// The question is the shared Confirm, unchanged, rendered into document.body
// (its buttons have no type, so inside the Ship <Form> they would submit it,
// see SP-028). Around it this hook adds what Confirm lacks: focus moves into
// it, Tab stays inside, Escape cancels and focus returns afterwards. While a
// question is open, further picks are ignored.
export default function useWarehouseChange({
  Warehouses,
  SelectedProducts,
  Plist,
  setSelectedProducts,
  SetPlist,
  SetStepCheck,
}) {
  const user = useSelector((state) => state.user);
  const [pending, setPending] = useState(null);
  const dialogRef = useRef(null);
  const returnFocusRef = useRef(null);

  const productsShown = Boolean(Warehouses) && (SelectedProducts?.length ?? 0) > 0;
  const wouldRemoveSomething =
    productsShown || (Array.isArray(Plist) && Plist.some(rowHasData));

  const setters = { setSelectedProducts, SetPlist, SetStepCheck };

  const requestChange = (value) => {
    if (pending) return;
    if (wouldRemoveSomething) {
      returnFocusRef.current = document.activeElement;
      setPending({ value });
    } else {
      applyWarehouseChange(value, setters);
    }
  };

  useEffect(() => {
    if (!pending) return undefined;
    dialogRef.current?.querySelector("button")?.focus();
    return () => {
      const back = returnFocusRef.current;
      returnFocusRef.current = null;
      if (back && document.contains(back)) back.focus();
    };
  }, [pending]);

  const onKeyDown = (event) => {
    if (event.key === "Escape") {
      event.stopPropagation();
      setPending(null);
      return;
    }
    if (event.key !== "Tab") return;
    const buttons = Array.from(dialogRef.current?.querySelectorAll("button") ?? []);
    if (!buttons.length) return;
    const first = buttons[0];
    const last = buttons[buttons.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  const approved = Array.isArray(user?.user_approved_warehouses)
    ? user.user_approved_warehouses
    : [];
  const target = pending
    ? approved.find((entry) => String(entry?.warehouse_id) === String(pending.value))
    : null;
  // "Use X" for a first pick (e.g. reshipped rows already there), "Change
  // to X" when a location is already chosen.
  const first = !Warehouses;
  const question = `${
    first
      ? `Use ${target ? locationName(target) : "this location"} as the fulfilment location?`
      : `Change the fulfilment location${target ? ` to ${locationName(target)}` : ""}?`
  } The products and packages you added will be removed.`;

  const confirmDialog = pending
    ? createPortal(
        <div
          ref={dialogRef}
          role="dialog"
          aria-modal="true"
          aria-label={question}
          onKeyDown={onKeyDown}
        >
          <Confirm
            open
            setOpen={(open) => {
              if (!open) setPending(null);
            }}
            handleConfirm={() => {
              applyWarehouseChange(pending.value, setters);
              setPending(null);
            }}
            text={question}
          />
        </div>,
        document.body,
      )
    : null;

  return { requestChange, confirmDialog };
}
